import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/api";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const StaffSetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [staff, setStaff] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      toast({
        title: t("staffSetPassword.invalidLink"),
        description: t("staffSetPassword.noToken"),
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      setLoading(true);
      const res = await api.staffVerifyInvitation(token);
      setValid(res.valid);
      setStaff(res.staff);
    } catch (err) {
      toast({
        title: t("staffSetPassword.invalidOrExpired"),
        description: err.message,
        variant: "destructive",
      });
      setValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: t("staffSetPassword.allFieldsRequired"),
        variant: "destructive",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: t("staffSetPassword.tooShortTitle"),
        description: t("staffSetPassword.tooShortDesc"),
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t("staffSetPassword.mismatch"),
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      await api.staffSetPassword({ token, password });
      toast({
        title: t("staffSetPassword.passwordSetTitle"),
        description: t("staffSetPassword.passwordSetDesc"),
      });
      setTimeout(() => {
        navigate("/staff/login");
      }, 1500);
    } catch (err) {
      toast({
        title: t("staffSetPassword.errorTitle"),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-muted-foreground">{t("staffSetPassword.checkingInvitation")}</div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-semibold mb-2">{t("staffSetPassword.invalidLink")}</h2>
            <p className="text-muted-foreground mb-4">
              {t("staffSetPassword.invalidOrExpired")}
            </p>
            <Button onClick={() => navigate("/staff/login")}>
              {t("staffSetPassword.goToLogin")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
          <CardTitle className="text-center">{t("staffSetPassword.setupTitle")}</CardTitle>
          {staff && (
            <p className="text-center text-sm text-muted-foreground mt-2">
              {t("staffSetPassword.welcome", { firstName: staff.firstName, lastName: staff.lastName })}
              <br />
              <span className="text-muted-foreground">{staff.email}</span>
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">{t("staffSetPassword.newPassword")}</label>
              <input
                type="password"
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("staffSetPassword.newPasswordPlaceholder")}
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">{t("staffSetPassword.confirmPassword")}</label>
              <input
                type="password"
                className="mt-1 w-full border rounded px-3 py-2 text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("staffSetPassword.confirmPasswordPlaceholder")}
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? t("staffSetPassword.saving") : t("staffSetPassword.setPassword")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffSetPasswordPage;
